import React, { useCallback, useEffect, useRef, useState } from "react";
import { useRecordCrumb } from "@/components/shell/ShellContext";
import { Link, useNavigate, useParams } from "react-router-dom";
import type { Editor } from "@tiptap/react";
import { AlertTriangle, ArrowLeft, CheckCircle2, Clock, Copy, Download, Eye, History, Info, Loader2, Mail, PenLine, FilePlus2, Save, Send, ShieldCheck, StopCircle, Trash2, UserCheck, XCircle } from "lucide-react";
import { api, unwrapObject } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/context/ConfirmContext";
import { FormSkeleton } from "@/components/ui/page-skeletons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SearchSelect } from "@/components/ui/search-select";
import { dateLocale, withClock } from "@/lib/dateTime";
import { formatINR } from "@/lib/utils";
import { AgreementStatusBadge } from "@/components/agreements/AgreementStatusBadge";
import { SectionsEditor, sectionProblem, toApiSections, toEditableSections, type EditableSection } from "@/components/proposals/SectionsEditor";
import { MergeFieldPanel } from "@/components/proposals/MergeFieldPanel";
import { AgreementDocument } from "@/components/agreements/AgreementDocument";
import { UploadedPdfViewer } from "@/components/proposals/UploadedPdfViewer";
import { SendAgreementDialog } from "@/components/agreements/SendAgreementDialog";
import { SigningDialog, type SigningValues } from "@/components/agreements/SigningDialog";
import { AGREEMENT_TYPE_LABELS, type AgreementRecord, type AgreementRender, type AgreementSigner, type AgreementType } from "@/types/agreement";

const fmtDate = (d?: string | null) =>
  d ? new Date(d.length === 10 ? `${d}T00:00:00` : d).toLocaleDateString(dateLocale(), { day: "2-digit", month: "short", year: "numeric" }) : "-";
const fmtDateTime = (d?: string | null) => (d ? new Date(d).toLocaleString(dateLocale(), withClock({ day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })) : "-");

export function AgreementEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { success, error } = useToast();
  const { hasPermission, user } = useAuth();
  const confirm = useConfirm();
  const canUpdate = hasPermission("agreement.update");

  const [a, setA] = useState<AgreementRecord | null>(null);
  useRecordCrumb(a ? a.title || a.display_number : undefined);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [title, setTitle] = useState("");
  const [type, setType] = useState<AgreementType>("SUPPLY");
  const [contactId, setContactId] = useState("");
  const [contactLabel, setContactLabel] = useState("");
  const [effective, setEffective] = useState("");
  const [endDate, setEndDate] = useState("");
  const [autoRenew, setAutoRenew] = useState(false);
  const [notice, setNotice] = useState("30");
  // The customer's own agreement (uploaded PDF): its contract value is typed, not taken from clauses
  const [contractValue, setContractValue] = useState("");
  const [amendOpen, setAmendOpen] = useState(false);
  const [amendEnd, setAmendEnd] = useState("");
  const [amendValue, setAmendValue] = useState("");
  const [amendSummary, setAmendSummary] = useState("");
  const [sections, setSections] = useState<EditableSection[]>([]);
  const [dirty, setDirty] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [reviewDialog, setReviewDialog] = useState<"approve" | "reject" | null>(null);
  const [comment, setComment] = useState("");
  const [terminateOpen, setTerminateOpen] = useState(false);
  const [terminateReason, setTerminateReason] = useState("");
  const [countersignOpen, setCountersignOpen] = useState(false);
  // A sent agreement is shown as its signers see it (its frozen copy)
  const [sentDoc, setSentDoc] = useState<AgreementRender | null>(null);
  const activeEditor = useRef<Editor | null>(null);

  const apply = useCallback((rec: AgreementRecord) => {
    setA(rec);
    setTitle(rec.title);
    setType(rec.agreement_type);
    setContactId(rec.contact_id || "");
    setContactLabel(rec.contact?.name || "");
    setEffective(rec.effective_date?.slice(0, 10) || "");
    setEndDate(rec.end_date?.slice(0, 10) || "");
    setAutoRenew(!!rec.auto_renew);
    setContractValue(String(Number(rec.contract_value || 0)));
    setNotice(String(rec.renewal_notice_days ?? 30));
    setSections(toEditableSections(rec.sections));
    setDirty(false);
    activeEditor.current = null;
  }, []);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      apply(unwrapObject<AgreementRecord>(await api.get(`/api/Agreement/ById/${id}`)));
    } catch (err: any) {
      error("Couldn't open the agreement", err.message || "Agreement not found");
      navigate("/agreements");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const editable = !!a?.editable && canUpdate;
  const showSent = !!a?.sent_at && !a.editable;
  const signedCount = a?.signers.filter((x) => x.signer_status === "SIGNED").length;
  useEffect(() => {
    if (!showSent || !a) {
      setSentDoc(null);
      return;
    }
    api
      .get(`/api/Agreement/Preview/${a.id}`)
      .then((res) => setSentDoc(unwrapObject<AgreementRender>(res)))
      .catch(() => setSentDoc(null));
    // Reloaded when the status or the signatures change
  }, [showSent, a?.id, a?.agreement_status, signedCount]);
  const markDirty = <T,>(set: (v: T) => void) => (v: T) => {
    set(v);
    setDirty(true);
  };

  const save = async (silent = false): Promise<boolean> => {
    if (!a) return false;
    const problem = !title.trim() ? "Give the agreement a title" : !effective || !endDate ? "Set when the agreement starts and ends" : endDate <= effective ? "The agreement must end after it starts" : a.source === "UPLOADED" ? null : sectionProblem(sections);
    if (problem) {
      error("Can't save yet", problem);
      return false;
    }
    try {
      const rec = unwrapObject<AgreementRecord>(
        await api.put(`/api/Agreement/Update/${a.id}`, {
          title: title.trim(),
          agreement_type: type,
          contact_id: contactId || undefined,
          effective_date: effective,
          end_date: endDate,
          auto_renew: autoRenew,
          renewal_notice_days: Math.max(0, Math.min(365, Math.round(Number(notice) || 0))),
          ...(a.source === "UPLOADED" ? { contract_value: Number(contractValue) || 0 } : { sections: toApiSections(sections) }),
        }),
      );
      apply(rec);
      if (!silent) success("Saved", "Agreement saved");
      return true;
    } catch (err: any) {
      error("Save failed", err.message || "Could not save the agreement");
      return false;
    }
  };

  // Ctrl/Cmd+S saves
  const saveRef = useRef(save);
  saveRef.current = save;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        if (editable && dirty && !busy) saveRef.current();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [editable, dirty, busy]);

  const openPdf = async () => {
    if (!a) return;
    setBusy(true);
    const ok = dirty ? await save(true) : true;
    setBusy(false);
    if (ok) window.open(`/api/Agreement/Pdf/${a.id}`, "_blank");
  };

  /** Runs an API call with the page locked; returns its response, or null after showing why it failed */
  const act = async (call: () => Promise<any>, title: string, message: string) => {
    setBusy(true);
    try {
      const res = await call();
      success(title, message);
      return res;
    } catch (err: any) {
      error("That didn't work", err.message || "Something went wrong");
      return null;
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    if (!a) return;
    if (dirty && !(await save(true))) return;
    const res = await act(() => api.post(`/api/Agreement/Submit/${a.id}`), "Submitted", "The approver has been asked to review it");
    if (res) apply(unwrapObject<AgreementRecord>(res));
  };

  const review = async () => {
    if (!a || !reviewDialog) return;
    if (reviewDialog === "reject" && !comment.trim()) return error("Say what needs changing", "Add a comment for the rep");
    const approve = reviewDialog === "approve";
    const res = await act(
      () => api.post(`/api/Agreement/${approve ? "Approve" : "Reject"}/${a.id}`, { comment: comment.trim() || undefined }),
      approve ? "Approved" : "Sent back",
      approve ? "The rep can send it for signing now" : "The rep has been told what to change",
    );
    if (res) {
      apply(unwrapObject<AgreementRecord>(res));
      setReviewDialog(null);
    }
  };

  /** Swaps the customer's PDF for a corrected one (while the agreement can still be edited) */
  const replaceFile = async (file: File) => {
    if (!a) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("deal_id", a.deal_id);
      fd.append("file", file);
      const up: any = await api.upload("/api/Upload/File", fd);
      const stored = up?.AddtionalData || up?.data || up;
      const rec = unwrapObject<AgreementRecord>(await api.put(`/api/Agreement/Update/${a.id}`, { file_url: stored?.url, file_name: stored?.original_name || file.name }));
      apply(rec);
      success("PDF replaced", "The agreement now uses the new file");
    } catch (err: any) {
      error("Couldn't replace the PDF", err.message || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const amend = async () => {
    if (!a) return;
    if (!amendEnd && amendValue === "" && !amendSummary.trim()) return error("Say what changes", "Enter a new end date, a new contract value, or describe the change");
    const res = await act(
      () => api.post(`/api/Agreement/Amend/${a.id}`, { ...(amendEnd ? { end_date: amendEnd } : {}), ...(amendValue !== "" ? { contract_value: Number(amendValue) } : {}), ...(amendSummary.trim() ? { summary: amendSummary.trim() } : {}) }),
      "Amendment started",
      "Review it, then send it for signing. The agreement changes once it is signed.",
    );
    if (res) {
      setAmendOpen(false);
      navigate(`/agreements/${unwrapObject<AgreementRecord>(res).id}`);
    }
  };

  const terminate = async () => {
    if (!a) return;
    if (!terminateReason.trim()) return error("Say why", "Add the reason the agreement is being ended");
    const res = await act(() => api.post(`/api/Agreement/Terminate/${a.id}`, { reason: terminateReason.trim() }), "Agreement ended", `${a.display_number} was ended`);
    if (res) {
      apply(unwrapObject<AgreementRecord>(res));
      setTerminateOpen(false);
      setTerminateReason("");
    }
  };

  const newVersion = async () => {
    if (!a) return;
    if (!(await confirm({ title: "Make a new version?", message: `${a.display_number} will be replaced by a new draft. ${a.signers.some((s) => s.signer_status === "SIGNED") ? "Signatures already on it don't carry over: everyone signs the new version. " : ""}The old one is kept as a record.`, confirmLabel: "Make new version" }))) return;
    const res = await act(() => api.post(`/api/Agreement/NewVersion/${a.id}`), "New version", "A new draft was made; the old one is kept as a record");
    if (res) navigate(`/agreements/${unwrapObject<AgreementRecord>(res).id}`);
  };

  const remove = async () => {
    if (!a) return;
    if (!(await confirm({ title: "Delete this draft?", message: `${a.display_number} will be removed. This can't be undone.`, confirmLabel: "Delete", destructive: true }))) return;
    setBusy(true);
    try {
      await api.delete(`/api/Agreement/Delete/${a.id}`);
      success("Deleted", `${a.display_number} deleted`);
      setDirty(false);
      navigate(`/deals/${a.deal_id}`);
    } catch (err: any) {
      error("Couldn't delete", err.message || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  if (loading || !a) return <FormSkeleton />;

  // [Bracketed] template text still to be replaced, counted on what is on screen
  const isUploaded = a.source === "UPLOADED";
  const placeholders = isUploaded ? 0 : (([title, ...sections.flatMap((s) => [s.title, s.html || ""])].join(" ")).match(/\[[^\[\]\n<>]{3,}\]/g) || []).length;
  const c = a.commercial_snapshot;

  return (
    <div className="space-y-4 pb-10">
      <section aria-label="Agreement summary" className="rounded-lg border border-border bg-card px-4 py-3.5">
        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
          <div className="space-y-1 min-w-0">
            <Link to={`/deals/${a.deal_id}`} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
              <ArrowLeft className="size-3.5" /> {a.deal.name}
            </Link>
            <div className="flex items-center gap-2.5 flex-wrap">
              <h1 className="text-[19px] font-semibold leading-tight tracking-tight">{a.title || a.display_number}</h1>
              <AgreementStatusBadge status={a.agreement_status} />
              {dirty && <span className="text-xs text-warning">Unsaved changes</span>}
            </div>
            <p className="text-[13px] text-muted-foreground">
              <span className="font-mono text-xs">{a.display_number}</span>
              {a.account?.name && (
                <>
                  {" · "}
                  <span className="font-medium text-foreground">{a.account.name}</span>
                </>
              )}
              {` · ${fmtDate(a.effective_date)} → ${fmtDate(a.end_date)}`}
              {a.owner && ` · ${a.owner.name}`}
              {a.template && ` · from "${a.template.name}"`}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-right mr-2">
              <div className="text-[11px] text-muted-foreground">Contract value</div>
              <div className="text-base font-bold">{formatINR(a.contract_value)}</div>
            </div>
            <Button variant="outline" size="sm" className="gap-1.5" onClick={openPdf} disabled={busy}>
              <Download className="size-4" /> {a.agreement_status === "EXECUTED" && !isUploaded ? "Signed PDF" : isUploaded ? "Agreement PDF" : "PDF"}
            </Button>
            {a.can_delete && canUpdate && (
              <Button variant="outline" size="sm" className="gap-1.5 text-destructive" onClick={remove} disabled={busy}>
                <Trash2 className="size-4" /> Delete
              </Button>
            )}
            {a.can_submit && canUpdate && (
              <Button size="sm" className="gap-1.5" onClick={submit} disabled={busy}>
                <ShieldCheck className="size-4" /> Submit for approval
              </Button>
            )}
            {a.can_review && (
              <>
                <Button variant="outline" size="sm" className="gap-1.5" onClick={() => (setComment(""), setReviewDialog("reject"))} disabled={busy}>
                  <XCircle className="size-4" /> Send back
                </Button>
                <Button size="sm" className="gap-1.5" onClick={() => (setComment(""), setReviewDialog("approve"))} disabled={busy}>
                  <CheckCircle2 className="size-4" /> Approve
                </Button>
              </>
            )}
            {a.can_new_version && canUpdate && (
              <Button variant="outline" size="sm" className="gap-1.5" onClick={newVersion} disabled={busy}>
                <Copy className="size-4" /> New version
              </Button>
            )}
            {a.can_amend && canUpdate && (
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => (setAmendEnd(""), setAmendValue(""), setAmendSummary(""), setAmendOpen(true))} disabled={busy}>
                <FilePlus2 className="size-4" /> Amend
              </Button>
            )}
            {isUploaded && a.agreement_status === "EXECUTED" && (
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => window.open(`/api/Agreement/Certificate/${a.id}`, "_blank")}>
                <ShieldCheck className="size-4" /> Certificate
              </Button>
            )}
            {a.can_terminate && (
              <Button variant="outline" size="sm" className="gap-1.5 text-destructive" onClick={() => (setTerminateReason(""), setTerminateOpen(true))} disabled={busy}>
                <StopCircle className="size-4" /> End agreement
              </Button>
            )}
            {a.can_countersign && (
              <Button size="sm" className="gap-1.5" onClick={() => setCountersignOpen(true)} disabled={busy}>
                <PenLine className="size-4" /> Countersign
              </Button>
            )}
            {a.can_send && !dirty && (
              <Button size="sm" className="gap-1.5" onClick={() => setSendOpen(true)} disabled={busy}>
                <Send className="size-4" /> Send for signing
              </Button>
            )}
            {editable && (
              <Button variant={a.can_send && !dirty ? "outline" : "default"} size="sm" className="gap-1.5" onClick={() => (setBusy(true), save().finally(() => setBusy(false)))} disabled={busy || !dirty}>
                {busy ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />} Save
              </Button>
            )}
          </div>
        </div>
      </section>

      {editable && placeholders > 0 && (
        <div role="status" className="flex gap-2 rounded-xl border border-warning/30 bg-warning-soft px-4 py-3 text-sm text-warning">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          <div>{placeholders} [bracketed] placeholder{placeholders === 1 ? "" : "s"} from the template still need replacing before this goes to the customer.</div>
        </div>
      )}
      {a.amends && (
        <div role="status" className="flex gap-2 rounded-xl border border-primary/20 bg-primary/10 px-4 py-3 text-sm text-primary">
          <FilePlus2 className="mt-0.5 size-4 shrink-0" />
          <div>
            Amendment to agreement{" "}
            <Link to={`/agreements/${a.amends.id}`} className="font-mono font-semibold underline">{a.amends.display_number}</Link>
            {a.amendment_changes?.end_date ? ` · new end date ${fmtDate(a.amendment_changes.end_date)}` : ""}
            {a.amendment_changes?.contract_value !== undefined ? ` · new value ${formatINR(a.amendment_changes.contract_value)}` : ""}.{" "}
            {a.amendment_applied_at ? `Signed and applied on ${fmtDate(a.amendment_applied_at)}.` : "The agreement changes once everyone has signed this."}
          </div>
        </div>
      )}
      {a.agreement_status === "EXECUTED" && a.days_left !== null && !a.amends && (
        <div role="status" className={`flex gap-2 rounded-xl border px-4 py-3 text-sm ${a.days_left <= a.renewal_notice_days ? "border-warning/30 bg-warning-soft text-warning" : "border-success/30 bg-success-soft text-success"}`}>
          <CheckCircle2 className="mt-0.5 size-4 shrink-0" />
          <div>
            In force until {fmtDate(a.end_date)}
            {a.days_left >= 0 ? ` (${a.days_left} day${a.days_left === 1 ? "" : "s"} left)` : ""}.{" "}
            {a.auto_renew ? "It renews itself for another term when it ends." : a.days_left <= a.renewal_notice_days ? "It will expire unless it is renewed: talk to the customer about a new agreement." : "It expires when it ends unless a new one is made."}
            {a.renewed_count > 0 ? ` Renewed ${a.renewed_count} time${a.renewed_count === 1 ? "" : "s"} so far.` : ""}
          </div>
        </div>
      )}
      {a.agreement_status === "EXPIRED" && (
        <div role="status" className="flex gap-2 rounded-xl border border-danger/30 bg-danger-soft px-4 py-3 text-sm text-danger">
          <XCircle className="mt-0.5 size-4 shrink-0" />
          <div>This agreement ended on {fmtDate(a.end_date)} and was not renewed. Make a new version to start again.</div>
        </div>
      )}
      {a.agreement_status === "TERMINATED" && (
        <div role="status" className="flex gap-2 rounded-xl border border-danger/30 bg-danger-soft px-4 py-3 text-sm text-danger">
          <XCircle className="mt-0.5 size-4 shrink-0" />
          <div>
            Ended early on {fmtDate(a.terminated_at)}
            {a.termination_reason ? `: ${a.termination_reason}` : ""}. Orders already made are not affected.
          </div>
        </div>
      )}
      {a.agreement_status === "IN_REVIEW" && (
        <div role="status" className="flex gap-2 rounded-xl border border-warning/30 bg-warning-soft px-4 py-3 text-sm text-warning">
          <Clock className="mt-0.5 size-4 shrink-0" />
          <div>Waiting for {a.pending_reviewer?.name || "an approver"} to review it. It can be edited again once it's reviewed.</div>
        </div>
      )}
      {a.agreement_status === "DRAFT" && a.review_comment && (
        <div role="status" className="flex gap-2 rounded-xl border border-warning/30 bg-warning-soft px-4 py-3 text-sm text-warning">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          <div>
            <span className="font-semibold">{a.reviewed_by?.name || "The reviewer"} sent it back:</span> {a.review_comment}
          </div>
        </div>
      )}
      {a.agreement_status === "DRAFT" && a.requires_review && !a.review_comment && canUpdate && (
        <div role="status" className="flex gap-2 rounded-xl border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
          <ShieldCheck className="mt-0.5 size-4 shrink-0" />
          <div>This agreement needs approval before it can be sent. Submit it when it's ready.</div>
        </div>
      )}
      {a.agreement_status === "APPROVED" && (
        <div role="status" className="flex gap-2 rounded-xl border border-success/30 bg-success-soft px-4 py-3 text-sm text-success">
          <CheckCircle2 className="mt-0.5 size-4 shrink-0" />
          <div>Approved{a.reviewed_by ? ` by ${a.reviewed_by.name}` : ""}{a.review_comment ? `: ${a.review_comment}` : ""}. It can be sent for signing. Editing it again needs a new approval.</div>
        </div>
      )}
      {!a.editable && a.agreement_status !== "IN_REVIEW" && (
        <div role="status" className="flex gap-2 rounded-xl border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
          <Info className="mt-0.5 size-4 shrink-0" />
          <div>{a.sent_at ? "This agreement was sent, so what the signers see can't change." : "This agreement can't be edited any more."} {a.can_new_version ? "Make a new version to change it." : ""}</div>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 items-start">
        {showSent ? (
          <div className="xl:col-span-8 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-sm font-bold">As sent to the signers</h3>
              <span className="text-xs text-muted-foreground">Addressed to {a.contact?.name || "-"}</span>
            </div>
            {isUploaded ? (
              <UploadedPdfViewer src={`/api/Agreement/Pdf/${a.id}?v=${a.file_sha256 || ""}`} fileName={a.file_name} fileSize={a.file_size} canReplace={false} onReplace={() => undefined} hint="The customer's own PDF: this is exactly what the signers sign." />
            ) : (
              <div className="rounded-2xl bg-muted/50 p-3 sm:p-4">{sentDoc ? <AgreementDocument doc={sentDoc} /> : <div className="py-16 text-center text-sm text-muted-foreground">Loading…</div>}</div>
            )}
          </div>
        ) : (
        <div className="xl:col-span-8 space-y-4">
          <div className="bg-card border border-border rounded-lg p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="space-y-1 md:col-span-3">
              <Label className="text-xs">Title</Label>
              <Input value={title} disabled={!editable} maxLength={200} onChange={(e) => markDirty(setTitle)(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Kind</Label>
              <select
                value={type}
                disabled={!editable}
                onChange={(e) => markDirty(setType)(e.target.value as AgreementType)}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm shadow-xs disabled:opacity-60"
              >
                {(Object.keys(AGREEMENT_TYPE_LABELS) as AgreementType[]).map((k) => (
                  <option key={k} value={k}>{AGREEMENT_TYPE_LABELS[k]}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label className="text-xs">Addressed to</Label>
              {editable ? (
                <SearchSelect
                  endpoint="/api/Contact/List"
                  extraParams={{ account_id: a.account_id || undefined }}
                  value={contactId}
                  selectedLabel={contactLabel}
                  getLabel={(row) => `${row.first_name || ""} ${row.last_name || ""}`.trim() + (row.email ? ` (${row.email})` : "")}
                  placeholder="Search the account's contacts"
                  onChange={(cid, row) => {
                    setContactId(cid);
                    setContactLabel(row ? `${row.first_name || ""} ${row.last_name || ""}`.trim() : "");
                    setDirty(true);
                  }}
                />
              ) : (
                <div className="text-sm h-9 flex items-center">{a.contact ? `${a.contact.name}${a.contact.email ? ` (${a.contact.email})` : ""}` : "-"}</div>
              )}
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Starts</Label>
              <Input type="date" value={effective} disabled={!editable} onChange={(e) => markDirty(setEffective)(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Ends</Label>
              <Input type="date" value={endDate} disabled={!editable} onChange={(e) => markDirty(setEndDate)(e.target.value)} />
            </div>
            <label className="md:col-span-2 flex items-center gap-2 text-sm">
              <input type="checkbox" checked={autoRenew} disabled={!editable} onChange={(e) => markDirty(setAutoRenew)(e.target.checked)} className="size-4 rounded border-input" />
              Renews automatically at the end of the term
            </label>
            <div className="space-y-1 md:col-span-2">
              <Label className="text-xs">Remind the owner before it ends (days)</Label>
              <Input type="number" min={0} max={365} value={notice} disabled={!editable} onChange={(e) => markDirty(setNotice)(e.target.value)} />
            </div>
            {isUploaded && (
              <div className="space-y-1 md:col-span-2">
                <Label className="text-xs">Contract value, GST included (₹)</Label>
                <Input type="number" min={0} step="any" value={contractValue} disabled={!editable} onChange={(e) => markDirty(setContractValue)(e.target.value)} />
              </div>
            )}
          </div>

          {isUploaded && (
            <UploadedPdfViewer
              src={`/api/Agreement/Pdf/${a.id}?v=${a.file_sha256 || ""}`}
              fileName={a.file_name}
              fileSize={a.file_size}
              canReplace={editable}
              busy={busy}
              onReplace={replaceFile}
              hint="The customer's own PDF. Everyone signs this exact file; its signature certificate is made when the last person signs."
            />
          )}

          {!isUploaded && (
          <SectionsEditor
            allowFacts
            sections={sections}
            onChange={markDirty(setSections)}
            disabled={!editable}
            activeEditor={activeEditor}
            renderExtra={(s) =>
              s.type === "estimate" && c ? (
                <div className="rounded-lg border bg-muted/30 px-3 py-2 text-xs space-y-1">
                  <div className="font-semibold text-muted-foreground">
                    Price schedule from quotation {c.quote_number} (fixed; change the quotation to change prices)
                  </div>
                  <ul className="space-y-0.5">
                    {c.lines.map((l, i) => (
                      <li key={i} className="flex justify-between gap-3">
                        <span>{l.description} <span className="text-muted-foreground">× {l.quantity}{l.unit ? ` ${l.unit}` : ""}</span></span>
                        <span className="tabular-nums">{formatINR(l.line_total)}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="flex justify-between gap-3 border-t pt-1 font-semibold">
                    <span>Total incl. GST</span>
                    <span className="tabular-nums">{formatINR(c.grand_total)}</span>
                  </div>
                </div>
              ) : s.type === "payment" && a.payment?.length ? (
                <div className="rounded-lg border bg-muted/30 px-3 py-2 text-xs">
                  <div className="mb-1 font-semibold text-muted-foreground">What the customer will see (from the total of {formatINR(a.contract_value)})</div>
                  <ul className="space-y-0.5">
                    {a.payment.map((r, i) => (
                      <li key={i}><strong>{r.percent}% {r.label}</strong> - {formatINR(r.amount)}</li>
                    ))}
                  </ul>
                </div>
              ) : null
            }
          />
          )}
        </div>
        )}

        <div className="xl:col-span-4 space-y-4 xl:sticky xl:top-4">
          {editable && !isUploaded && <MergeFieldPanel fields={a.merge_fields} activeEditor={activeEditor} />}
          {a.amendments.length > 0 && (
            <div className="bg-card border border-border rounded-lg p-4 space-y-2 text-sm">
              <h3 className="text-sm font-bold flex items-center gap-2">
                <FilePlus2 className="size-4 text-primary" /> Amendments
              </h3>
              <ul className="space-y-1.5">
                {a.amendments.map((m) => (
                  <li key={m.id} className="text-xs">
                    <div className="flex items-center justify-between gap-2">
                      <Link to={`/agreements/${m.id}`} className="font-mono text-primary hover:underline">{m.display_number}</Link>
                      <AgreementStatusBadge status={m.agreement_status} />
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      {[m.amendment_changes?.end_date ? `ends ${fmtDate(m.amendment_changes.end_date)}` : null, m.amendment_changes?.contract_value !== undefined ? `value ${formatINR(m.amendment_changes.contract_value)}` : null, m.amendment_changes?.summary || null].filter(Boolean).join(" · ")}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {a.signers.length > 0 && <SignersCard a={a} canSend={hasPermission("agreement.send")} onChanged={load} />}

          <div className="bg-card border border-border rounded-lg p-4 space-y-2.5 text-sm">
            <h3 className="text-sm font-bold flex items-center gap-2">
              <Info className="size-4 text-primary" /> Details
            </h3>
            <Row label="Deal">
              <Link to={`/deals/${a.deal_id}`} className="text-primary hover:underline">{a.deal.name}</Link>
              {a.deal.stage && <span className="text-muted-foreground"> · {a.deal.stage}</span>}
            </Row>
            <Row label="Customer">{a.account?.name || "-"}</Row>
            <Row label="Quotation">
              {a.quotation_id ? <Link to={`/quotes/${a.quotation_id}`} className="text-primary hover:underline">{c?.quote_number || "Open"}</Link> : "-"}
            </Row>
            {c?.payment_terms && <Row label="Payment terms">{c.payment_terms}</Row>}
            <Row label="Owner">{a.owner?.name || "-"}</Row>
            <Row label="Created">{fmtDateTime(a.created_on)}</Row>
          </div>

          {a.orders.length > 0 && (
            <div className="bg-card border border-border rounded-lg p-4 space-y-2 text-sm">
              <h3 className="text-sm font-bold">Orders under this agreement</h3>
              <ul className="space-y-1.5">
                {a.orders.map((o) => (
                  <li key={o.id} className="flex items-center justify-between gap-2 text-xs">
                    <Link to={`/orders/${o.id}`} className="font-mono text-primary hover:underline">{o.order_number}</Link>
                    <span className="text-muted-foreground">{o.order_status.toLowerCase()} · {formatINR(o.grand_total)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {a.history.length > 0 && (
            <div className="bg-card border border-border rounded-lg p-4 space-y-2 text-sm">
              <h3 className="text-sm font-bold flex items-center gap-2">
                <History className="size-4 text-primary" /> History
              </h3>
              <ul className="space-y-1.5">
                {a.history.map((h) => (
                  <li key={h.id} className="text-xs">
                    <span className="font-semibold">{h.action.replace(/_/g, " ").toLowerCase()}</span>
                    {h.comment && <span className="text-muted-foreground"> · {h.comment}</span>}
                    <div className="text-[11px] text-muted-foreground">{fmtDateTime(h.created_on)}</div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      <Dialog open={amendOpen} onOpenChange={(o) => !busy && setAmendOpen(o)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Amend {a.display_number}</DialogTitle>
            <DialogDescription>An amendment is signed like an agreement. Once everyone has signed, its changes apply to this agreement; until then nothing changes.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">New end date (now {fmtDate(a.end_date)})</Label>
              <Input type="date" value={amendEnd} onChange={(e) => setAmendEnd(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">New contract value, GST included (now {formatINR(a.contract_value)})</Label>
              <Input type="number" min={0} step="any" value={amendValue} onChange={(e) => setAmendValue(e.target.value)} placeholder="Leave empty to keep it" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Anything else that changes</Label>
              <Textarea rows={3} maxLength={2000} value={amendSummary} onChange={(e) => setAmendSummary(e.target.value)} placeholder="e.g. Delivery within 10 days of each order" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAmendOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={amend} disabled={busy || (!amendEnd && amendValue === "" && !amendSummary.trim())}>
              {busy && <Loader2 className="size-4 animate-spin mr-2" />} Start amendment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={terminateOpen} onOpenChange={(o) => !busy && setTerminateOpen(o)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>End agreement {a.display_number}?</DialogTitle>
            <DialogDescription>It stops being in force today. Orders already made stay as they are; new orders will need a customer PO.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label className="text-xs">Why is it being ended? *</Label>
            <Textarea rows={3} maxLength={1000} value={terminateReason} onChange={(e) => setTerminateReason(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTerminateOpen(false)} disabled={busy}>Keep it</Button>
            <Button variant="destructive" onClick={terminate} disabled={busy || !terminateReason.trim()}>
              {busy && <Loader2 className="size-4 animate-spin mr-2" />} End agreement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve / send back */}
      <Dialog open={!!reviewDialog} onOpenChange={(o) => !o && setReviewDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{reviewDialog === "approve" ? "Approve agreement" : "Send back for changes"}</DialogTitle>
            <DialogDescription>
              {reviewDialog === "approve" ? `${a.owner?.name || "The rep"} can then send ${a.display_number} for signing.` : `Tell ${a.owner?.name || "the rep"} what to change. The agreement goes back to draft.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label className="text-xs">{reviewDialog === "approve" ? "Comment (optional)" : "What needs changing *"}</Label>
            <Textarea rows={4} maxLength={1000} value={comment} onChange={(e) => setComment(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewDialog(null)}>
              Cancel
            </Button>
            <Button onClick={review} disabled={busy || (reviewDialog === "reject" && !comment.trim())}>
              {busy && <Loader2 className="size-4 animate-spin mr-2" />}
              {reviewDialog === "approve" ? "Approve" : "Send back"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {a.can_send && <SendAgreementDialog open={sendOpen} onOpenChange={setSendOpen} agreement={a} onSent={(rec) => apply(rec)} />}
      {a.can_countersign && (
        <SigningDialog
          open={countersignOpen}
          onOpenChange={setCountersignOpen}
          heading={`Countersign ${a.display_number}`}
          description="The customer has signed. Your signature puts the agreement in force."
          defaultName={[user?.FirstName, user?.LastName].filter(Boolean).join(" ")}
          nameLocked
          statement={`I, [your name], sign agreement ${a.display_number} "${a.title}" on behalf of the company.\nI confirm that I am authorised to sign it, that I have read it, and that my typed or drawn signature is my electronic signature.`}
          submitLabel="Countersign"
          onSubmit={async (v: SigningValues) => {
            const rec = unwrapObject<AgreementRecord>(await api.post(`/api/Agreement/Countersign/${a.id}`, { signature_type: v.signature_type, signature_image: v.signature_image, signer_title: v.signer_title, agree: v.agree }));
            apply(rec);
            setCountersignOpen(false);
            success("Agreement in force", `${rec.display_number} is signed by everyone`);
          }}
        />
      )}
    </div>
  );
}

const SIGNER_ICON: Record<string, React.ElementType> = { SIGNED: CheckCircle2, DECLINED: XCircle, VIEWED: Eye, PENDING: Clock };

/** Who has to sign, in order, with their progress; the rep can email or copy a link for the signer whose turn it is */
function SignersCard({ a, canSend, onChanged }: { a: AgreementRecord; canSend: boolean; onChanged: () => void }) {
  const { success, error } = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  const out = ["SENT", "VIEWED", "PARTIALLY_SIGNED"].includes(a.agreement_status);
  const customers = a.signers.filter((s) => s.party === "CUSTOMER");
  const turn = customers.find((s) => s.signer_status !== "SIGNED");

  const makeLink = async (s: AgreementSigner, channel: "EMAIL" | "LINK") => {
    setBusy(`${s.id}:${channel}`);
    try {
      const res = unwrapObject<{ url: string | null; via: string }>(await api.post(`/api/Agreement/SignerLink/${a.id}/${s.id}`, { channel }));
      if (channel === "LINK" && res.url) {
        try {
          await navigator.clipboard.writeText(res.url);
          success("Link copied", `The signing link for ${s.signer_name} is on your clipboard. The old one no longer works.`);
        } catch {
          window.prompt("Signing link", res.url);
        }
      } else success("Sent", `${s.signer_name} was emailed a new link`);
      onChanged();
    } catch (err: any) {
      error("Couldn't make the link", err.message || "Something went wrong");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 space-y-3 text-sm">
      <h3 className="text-sm font-bold flex items-center gap-2">
        <UserCheck className="size-4 text-primary" /> Signers
      </h3>
      <ol className="space-y-3">
        {a.signers.map((s) => {
          const Icon = SIGNER_ICON[s.signer_status] || Clock;
          const mine = out && canSend && s.party === "CUSTOMER" && turn?.id === s.id;
          return (
            <li key={s.id} className="space-y-1">
              <div className="flex items-start gap-2">
                <Icon className={`size-4 mt-0.5 shrink-0 ${s.signer_status === "SIGNED" ? "text-success" : s.signer_status === "DECLINED" ? "text-danger" : "text-muted-foreground"}`} />
                <div className="min-w-0 flex-1">
                  <div className="font-medium leading-tight">{s.signer_name}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {s.party === "COMPANY" ? "For the company" : "For the customer"}
                    {s.signer_title ? ` · ${s.signer_title}` : ""}
                    {s.signer_status === "SIGNED" && s.signed_at ? ` · signed ${new Date(s.signed_at).toLocaleString()}` : s.signer_status === "DECLINED" ? " · declined" : turn?.id === s.id && out ? " · their turn" : ""}
                  </div>
                  {s.link && s.signer_status !== "SIGNED" && (
                    <div className="text-[11px] text-muted-foreground">
                      {s.link.channel === "EMAIL" ? `Emailed to ${s.link.recipient}` : "Link made for you to share"}
                      {s.link.view_count > 0 ? ` · opened ${s.link.view_count}×` : " · not opened yet"}
                    </div>
                  )}
                </div>
              </div>
              {mine && (
                <div className="flex gap-1.5 pl-6">
                  {s.signer_email && (
                    <button type="button" disabled={!!busy} onClick={() => makeLink(s, "EMAIL")} className="inline-flex h-7 items-center gap-1 rounded-md border px-2 text-[11px] font-medium hover:bg-muted disabled:opacity-60">
                      {busy === `${s.id}:EMAIL` ? <Loader2 className="size-3 animate-spin" /> : <Mail className="size-3" />} Email link
                    </button>
                  )}
                  <button type="button" disabled={!!busy} onClick={() => makeLink(s, "LINK")} className="inline-flex h-7 items-center gap-1 rounded-md border px-2 text-[11px] font-medium hover:bg-muted disabled:opacity-60">
                    {busy === `${s.id}:LINK` ? <Loader2 className="size-3 animate-spin" /> : <Copy className="size-3" />} Copy new link
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span className="text-right min-w-0">{children}</span>
    </div>
  );
}
